import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import { ApiError } from "../../api/client";
import { downloadIntakeFile, intakeDocumentQueryOptions } from "../../api/intake";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { buttonClassName } from "../../components/ui/button-styles";
import { DocumentDetails } from "./-components/document-details";
import { DocumentHistoryCard } from "./-components/document-history";
import { DocumentPartiesCard } from "./-components/document-parties";
import { DocumentPaymentsCard } from "./-components/document-payments";
import { DocumentPreview } from "./-components/document-preview";

const documentSearchSchema = z.object({
	/**
	 * Opens the drawer that links the document to a party of the Directory, for an editor of it: the Work page's item
	 * for a document to link sends an editor here with it.
	 */
	link: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute("/intake/$fileId")({
	validateSearch: documentSearchSchema,
	// Only the document is read ahead. Hovering a link preloads this route, and the preview's URL is what the API
	// records as a view, so `DocumentPreview` asks for it once the page is actually open.
	loader: async ({ context, params }) => {
		try {
			return await context.queryClient.query({
				...intakeDocumentQueryOptions(params.fileId),
				staleTime: "static",
			});
		} catch (error) {
			// The upload was turned away as a duplicate, or the id was typed by hand: the router's not-found page says so.
			if (error instanceof ApiError && error.status === 404) {
				notFound({ throw: true });
			}
			throw error;
		}
	},
	head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.name ?? "Document"} · Intake · Ravnar` }] }),
	staticData: { crumbs: ["Intake", "Document"] },
	component: DocumentPage,
});

function DocumentPage() {
	const { fileId } = Route.useParams();
	const { link } = Route.useSearch();
	const navigate = Route.useNavigate();
	const { data: document } = useSuspenseQuery(intakeDocumentQueryOptions(fileId));
	const [downloadFailed, setDownloadFailed] = useState<string | undefined>(undefined);
	const download = useMutation({
		mutationFn: () => downloadIntakeFile(document.id),
		onMutate: () => {
			setDownloadFailed(undefined);
		},
		onError: error => {
			setDownloadFailed(
				error instanceof ApiError ? error.message : `${document.name} could not be downloaded. Try again.`,
			);
		},
	});

	return (
		<>
			<PageHeader
				title={document.name}
				actions={
					<>
						<Link to="/intake" search={{}} className={buttonClassName({ size: "md" })}>
							Back to Intake
						</Link>
						<Button
							disabled={download.isPending}
							onClick={() => {
								download.mutate();
							}}
						>
							Download
						</Button>
					</>
				}
			/>
			{downloadFailed === undefined ? null : (
				<p role="alert" className="mb-3.5 text-caption text-alert">
					{downloadFailed}
				</p>
			)}
			{/*
				The dashboard's grid (DESIGN.md): the document takes the room, and its details, the parties it is linked to,
				its payments — for a reader of Finance — and its history — for an owner or an admin — stack in a 320 px
				column beside it.
			*/}
			<div className="grid items-start gap-3.5 lg:grid-cols-[minmax(0,1fr)_320px]">
				<DocumentPreview document={document} />
				<div className="flex flex-col gap-3.5">
					<DocumentDetails document={document} />
					<DocumentPartiesCard
						document={document}
						linking={link === true}
						onLinking={open => {
							// Opening is a step Back undoes; closing replaces it, as the Directory's Add party drawer does.
							void navigate({
								search: previous => ({ ...previous, link: open ? true : undefined }),
								replace: !open,
							});
						}}
					/>
					<DocumentPaymentsCard document={document} />
					<DocumentHistoryCard document={document} />
				</div>
			</div>
		</>
	);
}
